import type { TextFieldProps as MUITextFieldProps } from '@mui/material';
import { Box, TextField as MUITextField, Typography, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { forwardRef } from 'react';

import { fontSize } from '@verantos-dev/shared-ui-components/tokens';

import {
  TEXTFIELD_ERROR_ILL_FORMED_NUMBER,
  TEXTFIELD_ERROR_MAX_DIGITS_EXPONENT,
  TEXTFIELD_ERROR_MAX_DIGITS_MANTISSA,
  TEXTFIELD_ERROR_NUMERIC_MAX_VALUE,
  TEXTFIELD_ERROR_NUMERIC_MIN_VALUE,
  TEXTFIELD_ERROR_TEXT_INVALID_CHARACTERS,
  TEXTFIELD_ERROR_TEXT_MAX_LENGTH,
} from './errorCodes/TextFieldErrorCodes';
import { beep } from './helper/beep';
import { getWidthClass, getWidthStyle, WidthType } from './helper/computeWidth';

/**
 * Local Props for TextField shared component
 */
type LocalTextFieldProps = {
  /**
   * @prop {string} id - id of the entry, also used are the key
   */
  id: string;

  /**
   * @prop {displayLabel} displayLabel - display label of the TextField
   */
  displayLabel: string;

  /**
   * @prop {string} value - value of the TextField
   */
  value: string;

  /**
   * @prop {boolean} readOnly
   */
  readOnly?: boolean;

  /**
   * @prop {'xxlarge' | 'xlarge' | 'large' | 'medium' | 'small' | 'dialog' | 'auto' | number} width - size of the TextField
   * @default 'medium'
   */
  width?: WidthType;

  /**
   * @prop {boolean} inError - error state of the TextField
   * @default false
   */
  error?: boolean;

  /**
   * @prop {boolean} showErrorMsg - show error message of the TextField
   * @default false
   */
  showErrorMsg?: boolean;

  /**
   * @prop {string} maxCharacterErrorMsg - error message to display if maxCharacters is exceeded
   * @default 'Max characters exceeded'
   */
  maxCharacterErrorMsg?: string;

  /**
   * @prop {string} maxCharacterReachedMsg - message to add to the character count when the max characters reached
   * @default 'Max characters reached any additional character will be ignored
   */
  maxCharactersReachedMsg?: string;

  /**
   * @prop {boolean} beepMaxCharacters - beep when maxCharacters is reached
   * @default false
   */
  beepMaxCharacters?: boolean;

  /**
   * @prop {number} maxCharacters - max size of the TextField
   */
  maxCharacters?: number;

  /**
   * @prop {boolen} displayNumCharacter - display size of the TextField
   */
  displayNumCharacter?: boolean;

  /**
   * @prop {string} characterCountLabel - label used for character count
   * @default 'characters'
   */
  characterCountLabel?: string;

  /**
   * @prop {boolean} excludeLeadTailSpace - exclude leading & tailing space of the TextField
   * @default false
   */
  excludeLeadTailSpace?: boolean;

  /**
   * @prop {boolean} allowNegative - allow negative number of the TextField, integer and float only
   * @NOTE: This is only applicable to type integer and float
   * @default false
   */
  allowNegative?: boolean;

  /**
   * @prop {number} maxDigitsNumeric - max digits of the TextField, integer and float only
   * @NOTE: This is only applicable to type integer and float.
   * @NOTE: This is only applicable to the integer part of the number.
   * @NOTE: If negative the minus sign is not counted as a digit.
   * @default 8
   */
  maxDigitsNumeric?: number;

  /**
   * @prop {number} maxDigitsExponent - max digits of the exponent of the TextField, float only
   * @NOTE: This is only applicable to type float.
   * @NOTE: The decimal point is not counted as a digit.
   * @default 3
   */
  maxDigitsExponent?: number;

  /**
   * @prop {boolean} beepMaxDigits - beep when maxDigits is reached
   * @default false
   */
  beepMaxDigits?: boolean;

  /**
   * @prop {number} minNumericValue - min value of the TextField, integer and float only
   */
  minNumericValue?: number;

  /**
   * @prop {string} minNumericValueErrorMsg - error message to display if minNumericValue is exceeded
   * @default 'Min value exceeded'
   */
  minNumericValueErrorMsg?: string;

  /**
   * @prop {number} maxNumericValue - max value of the TextField, integer and float only
   */
  maxNumericValue?: number;

  /**
   * @prop {string} maxNumericValueErrorMsg - error message to display if maxNumericValue is exceeded
   * @default 'Max value exceeded'
   */
  maxNumericValueErrorMsg?: string;

  /**
   * @prop {RegExp} validCharacters - valid characters of the TextField
   */
  validCharacters?: RegExp;

  /**
   * @prop {boolean} beepInvalidCharacters - beep when invalid characters are entered
   */
  beepInvalidCharacters?: boolean;

  /**
   * @prop {string} invalidCharacterErrorMsg - error message to display if invalid characters are entered
   * @default 'Invalid character entered'
   */
  invalidCharacterErrorMsg?: string;

  /**
   * @prop {string} helperText - helper text of the TextField
   */
  helperText?: string;

  /**
   * @prop {callback} onChange - change handler
   * @param {string} id - id of the TextField
   * @param {string} value - value of the TextField
   * @param {boolean} inError - if the value is valid
   * @param {string} errorCode - error code of the TextField
   * @param {string} trimValue - if excludeLeadTailSpace is true, this is the value without leading & tailing spaces
   */
  onChange?: (
    id: string,
    value: string,
    inError: boolean,
    errorCode: string,
    trimValue: string
  ) => void;

  /**
   * @prop {callback} onKeyUp - each character key up handler
   * @param {string} id - id of the TextField
   * @param {string} key - key just typed of the TextField
   */
  onKeyUp?: (id: string, key: string) => void;

  /**
   * @prop {callback} onFocus - focus handler
   * @param {string} id - id of the TextField
   */
  onFocus?: (id: string) => void;

  /**
   * @prop {callback} onBlur - blur handler
   * @param {string} id - id of the TextField
   * @param {string} value - value of the TextField
   * @param {boolean} inError - if the value in eerror
   * @param {string} errorCode - error code of the TextField
   * @param {string} trimValue - if excludeLeadTailSpace is true, this is the value without leading & tailing spaces
   */
  onBlur?: (
    id: string,
    value: string,
    inError: boolean,
    errorCode: string,
    trimValue: string
  ) => void;
};

/**
 * Override props for MUI TextField - this allow the app's to control the behavior of the Button as needed
 * When overriding a prop, we should also omit the prop from the MuiButtonProps selection
 * to ensure that the prop def when viewed only shows our override
 */
type MUIOverriddenTextFieldProps = {
  /**
   * @prop {'outlined'} variant - variant of the TextField at this time only outlined is supported
   * @default 'outlined'
   */
  //variant?: 'outlined';

  /**
   * @prop {'password' | 'text' | 'number' | 'email' | 'integer' | 'float'} type - type of the TextField at this time
   * @default 'text'
   */
  type?: 'password' | 'text' | 'number' | 'email' | 'integer' | 'float';

  /**
   * @prop {boolean} viewMode - To display the TextField in the view mode
   * @default false
   * */
  viewMode?: boolean;
};

export type TextFieldProps = LocalTextFieldProps &
  MUIOverriddenTextFieldProps &
  Omit<
    MUITextFieldProps,
    | 'helperText'
    | 'label'
    | 'error'
    | 'color'
    | 'variant'
    | 'type'
    | 'onChange'
    | 'onKeyUp'
    | 'onFocus'
    | 'onBlur'
    | 'fullWidth'
    | 'value'
    | 'variant'
  >;

const CustomTextField = styled(MUITextField)(({ theme }) => ({
  '& .MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-root fieldset': {
    borderWidth: '1px',
    // TODO: no VDS token for this value — needs designer input (rgba(0,0,0,0.23))
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    // TODO: no VDS token for this value — needs designer input (#252A36)
    color: '#252A36',
    '& fieldset': {
      borderColor: theme.palette.neutral[100],
      borderWidth: 2,
    },
    '&:hover fieldset': {
      borderColor: theme.palette.common.black,
    },
    '&.Mui-focused fieldset': {
      borderWidth: 2,
    },
  },
  '& .MuiTextField-root .input::placeholder': {
    opacity: 1,
    color: 'red',
    WebkitTextFillColor: theme.palette.text.secondary,
  },

  '&. MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: theme.palette.text.disabled,
    WebkitTextFillColor: theme.palette.text.disabled,
  },
  '& .MuiInputBase-input.MuiOutlinedInput-input.Mui-disabled': {
    color: theme.palette.text.disabled,
    WebkitTextFillColor: theme.palette.text.disabled,
    fontStyle: 'italic',
  },
  '& .Mui-disabled .MuiOutlinedInput-notchedOutline': {
    // TODO: no VDS token for this value — needs designer input (rgba(0,0,0,0.23))
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderWidth: '1px',
  },
}));

const TextFieldContainer = styled(Box)(() => ({
  display: 'inline-block',
  '&.--width-full': {
    width: '100%',
  },
  '&.--width-xxlarge': {
    width: '684px',
  },
  '&.--width-xlarge': {
    width: '556px',
  },
  '&.--width-large': {
    width: '448px',
  },
  '&.--width-medium': {
    width: '330px',
  },
  '&.--width-small': {
    width: '212px',
  },
  '&.--width-dialog': {
    width: '400px',
  },
  '&.--width-auto': {
    width: '100%',
  },
}));

const TextFieldViewMode = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  fontFamily: 'Roboto, sans-serif',
}));

const TextFieldLabel = styled(Typography)(({ theme }) => ({
  fontSize: fontSize[14],
  fontWeight: 400,
  lineHeight: '24px',
  color: theme.palette.text.secondary,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

const TextFieldValue = styled(Typography)({
  fontSize: fontSize[16],
  fontWeight: 600,
  lineHeight: '24px',
  // TODO: no VDS token for this value — needs designer input (#252a36)
  color: '#252a36',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>((props, ref) => {
  const {
    id,
    width = 'large',
    type = 'text',
    displayLabel,
    value,
    placeholder,
    defaultValue,
    disabled,
    required,
    readOnly,
    displayNumCharacter,
    characterCountLabel = 'characters',
    excludeLeadTailSpace,
    maxCharacters,
    maxCharacterErrorMsg = 'Max characters exceeded',
    maxCharactersReachedMsg = 'Max characters reached any additional character will be ignored',
    beepMaxCharacters = false,
    allowNegative = false,
    maxDigitsNumeric = 8,
    maxDigitsExponent = 3,
    beepMaxDigits = false,
    minNumericValue,
    minNumericValueErrorMsg = 'Min value exceeded',
    maxNumericValue,
    maxNumericValueErrorMsg = 'Max value exceeded',
    helperText,
    size = 'medium',
    autoComplete = 'off',
    showErrorMsg = false, // display as error exceeded maxCharacters and invalid characters
    validCharacters,
    beepInvalidCharacters = false,
    invalidCharacterErrorMsg = 'Invalid characters entered',
    error,
    viewMode = false,
    onFocus,
    onBlur,
    onChange,
    onKeyUp,
    onKeyDown,
    sx,
    ...rest
  } = props;

  const theme = useTheme();

  const allowedExtraCharacters = [
    'Backspace',
    'Delete',
    'ArrowLeft',
    'ArrowRight',
    'Shift',
    'Control',
    'Alt',
    'Meta',
    'Escape',
    'Home',
    'End',
    'PageUp',
    'PageDown',
  ];

  const inError = (val: string): { inError: boolean; errorCode: string } => {
    if (type === 'integer' || type === 'float') {
      if (val === '-' || val === '.' || val === '-.') {
        return { inError: true, errorCode: TEXTFIELD_ERROR_ILL_FORMED_NUMBER };
      } else {
        return { inError: false, errorCode: '' };
      }
    }

    const v = excludeLeadTailSpace ? val.trim() : val;

    // see if the current value exceeds the maxCharacters
    if (maxCharacters && v.length > maxCharacters) {
      return { inError: true, errorCode: TEXTFIELD_ERROR_TEXT_MAX_LENGTH };
    }

    // see if there are any invalid characters on the value
    let error = false;
    if (validCharacters && val) {
      val.split('').some((c) => {
        if (!validCharacters.test(c)) {
          error = true;
          return true;
        } else return false;
      });
    }

    if (error) {
      return { inError: true, errorCode: TEXTFIELD_ERROR_TEXT_INVALID_CHARACTERS };
    }

    return { inError: false, errorCode: '' };
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === 'integer' || type === 'float') {
      let errorCode = '';

      // handle type integer and float
      const inEvent = e.target;
      let val = e.target.value;
      const [mantissa, exponent] = val.split('.');
      // there should always be a mantissa so check it length, make sure to not count the - if negative
      const mantissaLen = mantissa[0] === '-' ? mantissa.length - 1 : mantissa.length;
      if (mantissaLen > maxDigitsNumeric && !allowedExtraCharacters.includes(val)) {
        // set the value to the previous value
        val = value;

        const cursorLoc = inEvent.selectionStart ? inEvent.selectionStart - 1 : 0;
        // set the cursor to the previous position
        setTimeout(() => {
          inEvent.selectionStart = cursorLoc;
          inEvent.selectionEnd = cursorLoc;
        });

        if (beepMaxDigits) beep();

        // set the error code
        errorCode = TEXTFIELD_ERROR_MAX_DIGITS_MANTISSA;
      } else if (
        exponent &&
        exponent.length > maxDigitsExponent &&
        !allowedExtraCharacters.includes(val)
      ) {
        // set the value to the previous value
        val = value;
        const cursorLoc = inEvent.selectionStart ? inEvent.selectionStart - 1 : 0;
        // set the cursor to the previous position
        setTimeout(() => {
          inEvent.selectionStart = cursorLoc;
          inEvent.selectionEnd = cursorLoc;
        }, 0);

        if (beepMaxDigits) beep();

        // set the error code
        errorCode = TEXTFIELD_ERROR_MAX_DIGITS_EXPONENT;
      }

      if (errorCode !== '') {
        onChange && onChange(id, val, true, errorCode, val);
      } else {
        const error = inError(e.currentTarget.value);
        onChange && onChange(id, val, error.inError, error.errorCode, val);
      }
    } else {
      // handle type text - always enforce maxCharacters limit regardless of showErrorMsg
      let newValue = e.currentTarget.value;

      if (maxCharacters) {
        if (excludeLeadTailSpace) {
          // For excludeLeadTailSpace, check the trimmed length
          // Don't allow any characters (including spaces) if trimmed value would exceed limit
          const trimmedValue = newValue.trim();

          if (trimmedValue.length > maxCharacters) {
            // Truncate the trimmed value to maxCharacters, then add back leading spaces
            const leadingSpacesCount = newValue.length - newValue.trimStart().length;
            const truncatedTrimmed = trimmedValue.substring(0, maxCharacters);
            newValue = ' '.repeat(leadingSpacesCount) + truncatedTrimmed;
            e.currentTarget.value = newValue;
          }
        } else {
          if (newValue.length > maxCharacters) {
            newValue = newValue.substring(0, maxCharacters);
            e.currentTarget.value = newValue;
          }
        }
      }

      const error = inError(newValue);
      onChange &&
        onChange(
          id,
          newValue,
          error.inError,
          error.errorCode,
          excludeLeadTailSpace ? newValue.trim() : newValue
        );
    }
  };

  const onPasteHandler = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (type === 'integer' || type === 'float') {
      // For numeric types, let the onChange handler deal with it
      return;
    }

    if (maxCharacters) {
      const pastedText = e.clipboardData.getData('text');
      const target = e.target as HTMLInputElement;
      const currentValue = target.value;
      const selectionStart = target.selectionStart || 0;
      const selectionEnd = target.selectionEnd || 0;

      // Calculate the new value after paste
      const beforeSelection = currentValue.substring(0, selectionStart);
      const afterSelection = currentValue.substring(selectionEnd);
      let newValue = beforeSelection + pastedText + afterSelection;

      // Enforce maxCharacters limit
      if (excludeLeadTailSpace) {
        // For excludeLeadTailSpace, check if trimmed value would exceed limit
        const trimmedNewValue = newValue.trim();

        if (trimmedNewValue.length > maxCharacters) {
          // Truncate to ensure trimmed value doesn't exceed maxCharacters
          const leadingSpacesCount = newValue.length - newValue.trimStart().length;
          const truncatedTrimmed = trimmedNewValue.substring(0, maxCharacters);
          newValue = ' '.repeat(leadingSpacesCount) + truncatedTrimmed;

          e.preventDefault();

          // Update the input value directly
          target.value = newValue;

          // Set cursor position at the end of the truncated value
          const newCursorPos = newValue.length;
          setTimeout(() => {
            target.selectionStart = newCursorPos;
            target.selectionEnd = newCursorPos;
          }, 0);

          // Trigger onChange to update state
          const syntheticEvent = {
            target: target,
            currentTarget: target,
          } as React.ChangeEvent<HTMLInputElement>;
          onChangeHandler(syntheticEvent);

          if (beepMaxCharacters) {
            beep();
          }
        }
      } else {
        if (newValue.length > maxCharacters) {
          // Truncate the pasted text to fit within the limit
          const availableSpace = maxCharacters - beforeSelection.length - afterSelection.length;
          const truncatedPastedText = pastedText.substring(0, Math.max(0, availableSpace));
          newValue = beforeSelection + truncatedPastedText + afterSelection;

          e.preventDefault();

          // Update the input value directly
          target.value = newValue;

          // Set cursor position after the pasted content
          const newCursorPos = Math.min(selectionStart + truncatedPastedText.length, maxCharacters);
          setTimeout(() => {
            target.selectionStart = newCursorPos;
            target.selectionEnd = newCursorPos;
          }, 0);

          // Trigger onChange to update state
          const syntheticEvent = {
            target: target,
            currentTarget: target,
          } as React.ChangeEvent<HTMLInputElement>;
          onChangeHandler(syntheticEvent);

          if (beepMaxCharacters) {
            beep();
          }
        }
      }
    }
  };

  const onBlurHandier = (e: React.FocusEvent<HTMLInputElement>) => {
    if (type === 'integer' || type === 'float') {
      if (e.target.value === '-' || e.target.value === '.' || e.target.value === '-.') {
        onBlur &&
          onBlur(id, e.target.value, true, TEXTFIELD_ERROR_ILL_FORMED_NUMBER, e.target.value);
      } else if (type === 'integer') {
        if (maxNumericValue && parseInt(e.target.value, 10) > maxNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MAX_VALUE, e.target.value);
        } else if (minNumericValue && parseInt(e.target.value, 10) < minNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MIN_VALUE, e.target.value);
        } else {
          onBlur && onBlur(id, e.target.value, false, '', e.target.value);
        }
      } else if (type === 'float') {
        if (maxNumericValue && parseFloat(e.target.value) > maxNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MAX_VALUE, e.target.value);
        } else if (minNumericValue && parseFloat(e.target.value) < minNumericValue) {
          onBlur &&
            onBlur(id, e.target.value, true, TEXTFIELD_ERROR_NUMERIC_MIN_VALUE, e.target.value);
        } else {
          onBlur && onBlur(id, e.target.value, false, '', e.target.value);
        }
      }
    } else {
      const error = inError(e.currentTarget.value);
      onBlur &&
        onBlur(
          id,
          e.target.value,
          error.inError,
          error.errorCode,
          excludeLeadTailSpace ? e.currentTarget.value.trim() : e.currentTarget.value
        );
    }
  };

  const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // if the character just typed is invalid don't display it
    const target = e.target as HTMLInputElement;

    // Allow navigation with Cmd (Mac) or Ctrl (Windows/Linux)
    if (e.metaKey || e.ctrlKey) {
      return;
    }

    if (type === 'integer') {
      const reg = allowNegative ? /^-?\d*$/ : /^\d*$/;
      if (!reg.test(e.key) && !allowedExtraCharacters.includes(e.key)) {
        e.preventDefault();
      }

      // make sure there is a single - at the beginning of the value
      if (e.key === '-' && target.selectionStart !== 0) {
        e.preventDefault();
      } else if (e.key === '-' && target.selectionStart === 0 && value[0] === '-') {
        e.preventDefault();
      }
    } else if (type === 'float') {
      // check for allowed characters
      const reg = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
      if (!reg.test(e.key) && !allowedExtraCharacters.includes(e.key)) {
        e.preventDefault();
      }

      // make sure there is only a single decimal point
      if (e.key === '.' && value.includes('.')) {
        e.preventDefault();
      }

      // make sure there is a single - at the beginning of the value
      if (e.key === '-' && target.selectionStart !== 0) {
        e.preventDefault();
      } else if (e.key === '-' && target.selectionStart === 0 && value[0] === '-') {
        e.preventDefault();
      }
    } else {
      if (validCharacters && !showErrorMsg) {
        if (!validCharacters.test(e.key)) {
          if (beepInvalidCharacters && !allowedExtraCharacters.includes(e.key)) {
            beep();
          }
          e.preventDefault();
        }
      }

      // Prevent ALL characters (including spaces) when maxCharacters limit is reached
      // This prevents double-tap spacebar from adding periods and unnecessary spaces
      if (maxCharacters && !allowedExtraCharacters.includes(e.key)) {
        if (excludeLeadTailSpace) {
          // For excludeLeadTailSpace, check the trimmed length
          // Calculate what the new value would be after adding this character
          const selectionStart = target.selectionStart || 0;
          const selectionEnd = target.selectionEnd || 0;
          const beforeSelection = value.substring(0, selectionStart);
          const afterSelection = value.substring(selectionEnd);
          const newValue = beforeSelection + e.key + afterSelection;
          const newTrimmedValue = newValue.trim();

          // Check if adding this character would exceed the limit
          // Prevent ALL characters (including spaces) if trimmed content would exceed maxCharacters
          if (newTrimmedValue.length >= maxCharacters) {
            if (beepMaxCharacters) {
              beep();
            }
            e.preventDefault();
            return;
          }
        } else {
          // For regular text fields, check the actual length
          const currentLength = value.length;

          if (currentLength >= maxCharacters) {
            if (beepMaxCharacters) {
              beep();
            }
            e.preventDefault();
            return;
          }
        }
      }
    }
    onKeyDown && onKeyDown(e);
  };

  // compute the number of characters
  let numCharMsg = '';
  const val = excludeLeadTailSpace ? value.trim() : value;
  if (maxCharacters !== undefined && displayNumCharacter)
    numCharMsg = `${val.length}/${maxCharacters} ${characterCountLabel}`;
  else if (!maxCharacters && displayNumCharacter)
    numCharMsg = `${value !== undefined ? value.length : 0} ${characterCountLabel}`;

  // add the maxCharactersReachedMsg if the maxCharacters is reached
  if (maxCharactersReachedMsg && maxCharacters && val.length > maxCharacters) {
    if (displayNumCharacter) numCharMsg += ' - ';
    numCharMsg += `${maxCharactersReachedMsg}`;
  }

  // see if there is a maxCharacters error and showError is true
  let inputError = false;
  let inputErrorMsg = '';
  if (maxCharacters && showErrorMsg) {
    const v = excludeLeadTailSpace ? value.trim() : value;
    inputError = v.length > maxCharacters;
    inputErrorMsg =
      numCharMsg + (maxCharacterErrorMsg && inputError ? ` - ${maxCharacterErrorMsg}` : '');
  }

  // // see if there is a numeric error min or max value
  if ((type === 'integer' || type === 'float') && showErrorMsg) {
    if (type === 'float' && maxNumericValue && parseFloat(value) > maxNumericValue) {
      inputError = true;
      inputErrorMsg = maxNumericValueErrorMsg;
    } else if (type === 'float' && minNumericValue && parseFloat(value) < minNumericValue) {
      inputError = true;
      inputErrorMsg = minNumericValueErrorMsg;
    } else if (type === 'integer' && maxNumericValue && parseFloat(value) > maxNumericValue) {
      inputError = true;
      inputErrorMsg = maxNumericValueErrorMsg;
    } else if (type === 'integer' && minNumericValue && parseFloat(value) < minNumericValue) {
      inputError = true;
      inputErrorMsg = minNumericValueErrorMsg;
    } else {
      inputError = false;
      inputErrorMsg = '';
    }
  }

  // test to see if there is an invalid character error and showError is true
  if (showErrorMsg && validCharacters && value) {
    value.split('').some((c) => {
      if (!validCharacters.test(c)) {
        inputError = true;
        inputErrorMsg = invalidCharacterErrorMsg;

        return true;
      } else return false;
    });
  }

  const thereIsAnError = error || inputError;
  const computedType = type === 'integer' || type === 'float' ? 'text' : type;

  return (
    <TextFieldContainer
      data-testid={`textField-${id}-container`}
      className={getWidthClass(width)}
      style={getWidthStyle(width)}
    >
      {viewMode ? (
        <TextFieldViewMode
          id={`textField-${id}-container-viewMode`}
          data-testid={`text-field-view-mode-${id}`}
        >
          <TextFieldLabel>{displayLabel}</TextFieldLabel>
          <TextFieldValue>{value}</TextFieldValue>
        </TextFieldViewMode>
      ) : (
        <CustomTextField
          {...rest}
          ref={ref}
          id={`textField-${id}`}
          data-testid={`textField-${id}`}
          value={value}
          type={computedType}
          autoComplete={autoComplete}
          defaultValue={defaultValue ? defaultValue : undefined}
          label={displayLabel}
          placeholder={placeholder}
          helperText={error && helperText ? helperText : inputErrorMsg || numCharMsg || helperText}
          error={thereIsAnError}
          disabled={disabled}
          required={required}
          variant={'outlined'}
          InputProps={{
            readOnly: readOnly,
            'aria-readonly': readOnly,
          }}
          InputLabelProps={{ shrink: true, sx: { color: 'text.primary' } }}
          FormHelperTextProps={{
            sx: {
              color: thereIsAnError ? 'error.main' : undefined,
            },
          }}
          size={size}
          fullWidth
          onFocus={() => onFocus && onFocus(id)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => onBlurHandier(e)}
          onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) => onKeyUp && onKeyUp(id, e.key)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeHandler(e)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => onKeyDownHandler(e)}
          onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => onPasteHandler(e)}
          sx={{
            input: {
              color: 'text.primary',
              WebkitTextFillColor: theme.palette.text.primary,
              '&::placeholder': {
                opacity: 1,
                color: 'text.secondary',
                WebkitTextFillColor: theme.palette.text.secondary,
              },
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                // TODO: no VDS token for this value — needs designer input (rgba(0,0,0,0.23))
                borderColor: thereIsAnError ? theme.palette.error.main : 'rgba(0, 0, 0, 0.23)',
                borderWidth: '1px',
              },

              '&.Mui-focused fieldset': {
                borderColor: thereIsAnError ? theme.palette.error.main : theme.palette.primary.main,
                borderWidth: '2px',
              },
            },
            '& .MuiInputLabel-root': {
              color: thereIsAnError ? 'error.main' : 'text.primary',

              '&.Mui-focused': {
                color: thereIsAnError ? 'error.main' : 'text.primary',
              },
            },
            '& .MuiFormHelperText-root': {
              color: thereIsAnError ? 'error.main' : `${theme.palette.text.secondary}`,
              marginLeft: '4px',
              marginRight: '4px',
              '&.Mui-error': {
                color: `${theme.palette.error.main} !important`,
              },
              '&.Mui-disabled': {
                color: theme.palette.text.disabled,
                WebkitTextFillColor: theme.palette.text.disabled,
              },
            },
            ...sx,
          }}
        />
      )}
    </TextFieldContainer>
  );
});

export default TextField;
